import { useParams } from "react-router";
import { usePromotedTrials } from "@/hooks/usePromotedTrials";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-[10px] border border-[#F0F2F4] px-6 py-4 min-w-[140px]">
      <p className="text-[22px] font-bold text-[#1F2933] m-0">{value}</p>
      <p className="text-[12px] text-[#6B7280] m-0 mt-0.5">{label}</p>
    </div>
  );
}

export default function PlannedTrialHome() {
  const { planId } = useParams<{ planId: string }>();
  const { trials } = usePromotedTrials();
  const trial = trials.find((t) => t.planId === planId);
  const snapshot = trial?.snapshot;

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8">
      <h2 className="text-[22px] font-bold text-[#1F2933] mb-2">
        {trial?.name ?? "Trial Overview"}
      </h2>
      {trial && (
        <p className="text-[14px] text-[#6B7280] mb-8">
          {trial.phase && <>{trial.phase} · </>}
          {trial.indication}
        </p>
      )}

      {/* Published plan snapshot — frozen at publish time; edits made in Trial
          Planning after publishing do not show here until republished. */}
      {snapshot && (
        <>
          <div className="flex gap-4 mb-4 flex-wrap justify-center">
            <Stat
              label="Sites selected"
              value={`${snapshot.sites.filter((s) => s.status === "selected").length} of ${snapshot.sites.length}`}
            />
            <Stat
              label="Milestones dated"
              value={`${snapshot.milestones.filter((m) => m.start_date).length}/${snapshot.milestones.length}`}
            />
            {snapshot.targetEnrollment !== undefined && (
              <Stat
                label="Target enrollment"
                value={String(snapshot.targetEnrollment)}
              />
            )}
          </div>
          <p className="text-[12px] text-[#9CA3AF] mb-8">
            Published {fmtDate(trial.promotedAt)}
            {trial.sourceVersion &&
              ` · plan version of ${fmtDate(trial.sourceVersion)}`}
          </p>
        </>
      )}

      <p className="text-[14px] text-[#9CA3AF] max-w-md">
        This trial is in the planning stage. Connect data sources and configure
        your trial to get started.
      </p>
    </div>
  );
}
